// Copyright © Luware Solutions AG. All rights reserved.

import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { SelectedTeamState } from '@nimbus-global/features/shared/store/selected-team-state/selected-team.state';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';

/**
 * Provide either `permission` (single) OR `permissions` (multi-permission, single-role check).
 * Supplying neither always evaluates to false.
 */
export interface IHasSelectedTeamPermissionOptions {
  permission?: NimbusPermission;
  permissions?: NimbusPermission[];
  // When true, fall back to the tenant-wide `isGranted` check instead of the per-team lookup.
  ignoreTeamSelection?: boolean;
}

/**
 * Single source of truth for "does the current user have <permission> on the currently selected team?".
 * Used by the menu's `PermissionFilter` so selected-team menu entries consistently apply the same semantics.
 */
export function hasSelectedTeamPermission$(
  store: Store,
  permissionManagerService: IPermissionManagerService,
  options: IHasSelectedTeamPermissionOptions,
): Observable<boolean> {
  return combineLatest([store.select(SelectedTeamState.team), permissionManagerService.getPermissionsModel$()]).pipe(
    map(([team, permissionModel]) => {
      if (!permissionModel) {
        return false;
      }

      if (!team?.groupId || options.ignoreTeamSelection) {
        if (options.permission) {
          return permissionModel.isGranted(options.permission);
        }
        if (options.permissions?.length) {
          return permissionModel.areGranted(options.permissions);
        }
        return false;
      }

      if (options.permission) {
        return permissionModel.isGrantedForTeam(team.groupId, options.permission);
      }
      if (options.permissions?.length) {
        return permissionModel.areGrantedForTeam(team.groupId, options.permissions);
      }

      return false;
    }),
  );
}
